import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import DragZone from 'components/Shared/DragZone';
import { useDropzone } from 'react-dropzone';
import Select from 'react-select';
import { NextBtn, BackBtn } from 'components/Shared/Button';
import { fetcher } from 'api/client';
import { getParsedContractTypes } from 'api/actions/contracts';
import { useForm, Controller } from 'react-hook-form';
import useReferenceComponent from 'hooks/useReferenceComponent';
import ReferenceDocument from '../ReferenceDocument';
import { fileTypes, contractStatusOptions } from '../Constants/types';
import { Col, Row, Input, Label, FormFeedback, Form } from 'reactstrap';
import { StepContainer } from './StepContainer';
import { reactSelectStyles } from 'components/Shared/ReactSelectStyles'

const UploadDocumentZone = ({ error, setError, setFiles, files }) => {
    const { t } = useTranslation();

    const handleFile = async (files) => {
        setFiles(files);
        setError(false);
    };

    const onDrop = useCallback(handleFile, []);

    const { open, getInputProps } = useDropzone({
        accept: fileTypes,
        maxSize: 25000000,
        noKeyboard: true,
        multiple: true,
        onDropAccepted: (file) => {
            handleFile(file);
        },
        onDrop,
    });

    return (
        <>
            <DragZone
                imgSrc="/images/listing/createDocument/subir.svg"
                label={t('common.drag_file')}
                className=""
                acceptedFiles={fileTypes}
                multiple={true}
                handleFile={handleFile}
            />
            
            <span className="font-weight-bold">{t('sign.contract.attachment_save')}</span>
            {/* <div className="file_pragamtic_container" onClick={open}>
        <div>{t("contracts.new.search_document")}</div>
        <input {...getInputProps()} />
        <i className="fas fa-upload text-white"></i>
      </div> */}
            <div>
                <FormFeedback invalid={error ? 'true' : 'false'}> {t('errors.MissingFile')}</FormFeedback>
                {files.map((f) => (
                    <DocumentRow key={f.id} file={f} files={files} setFiles={setFiles} />
                ))}
            </div>
        </>
    );
};

function DocumentRow({ file, setFiles, files }) {
    const setFile = () => {
        setFiles(files.filter((f) => f.id !== file.id));
    };

    return (
        <div onClick={setFile}>
            <i className="fas fa-times text-black"></i>
            {file.name}
        </div>
    );
}

function UploadForm({ register, control, errors, files, handleNext, handleRefChange, reference, showReference }) {
    const { t } = useTranslation();

    const { data, isSuccess } = useQuery('contract-types', () => fetcher(getParsedContractTypes()));

    const contractStatusOptionsT = contractStatusOptions.map((a) => ({
        value: a.value,
        label: t(a.label),
    }));

    return (
        <Form onSubmit={handleNext}>
            {files.map((f, index) => (
                <div key={index}>
                    <small>{f.fileName}</small>

                    <Input
                        name={`name-${index}`}
                        defaultValue={f.name}
                        innerRef={register({ required: true })}
                        invalid={errors[`name-${index}`]}
                        placeholder={t('contracts.new.name_title')}
                    />

                    <FormFeedback invalid={errors[`name-${index}`]}>{t('contracts.new.name_required')}</FormFeedback>

                    <div className="mt-2">
                        <Controller
                            as={Select}
                            name={`type-${index}`}
                            control={control}
                            rules={{ required: true }}
                            cacheOptions
                            className="create-select"
                            options={
                                isSuccess &&
                                data?.options?.map((option) => ({
                                    label: option.name,
                                    value: option._id,
                                }))
                            }
                            placeholder={t('contracts.listing.type')}
                            noOptionsMessage={() => <div>{t('contracts.listing.no_contracts')}</div>}
                        />

                        <Controller
                            as={Select}
                            styles={reactSelectStyles}
                            name={`status-${index}`}
                            control={control}
                            rules={{ required: true }}
                            cacheOptions
                            className="create-select"
                            defaultValue={contractStatusOptionsT[0]}
                            options={contractStatusOptionsT}
                            placeholder={t('contracts.listing.header_status')}
                        />
                    </div>

                    <FormFeedback invalid={[`type-${index}`] || errors[`status-${index}`]}>
                        {t(`${errors[`type-${index}`] ? 'contracts.new.error_type' : 'contracts.listing.header_status_missing'}`)}
                    </FormFeedback>
                </div>
            ))}
            <ReferenceDocument onChange={handleRefChange} reference={reference} showReference={showReference} />
        </Form>
    );
}

const MultipleStep = ({ handleBack, onComplete, showReference }) => {
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const [nextStep, setNextStep] = useState(false);
    const [reference, handleRefChange] = useReferenceComponent();
    const [error, setError] = useState(null);
    const { register, handleSubmit, control, getValues, errors } = useForm({
        shouldFocusError: false,
    });

    const handleNext = () => {
        if (files.length) {
            if (nextStep) {
                if (reference.join && reference.documentMissing) {
                    return;
                }

                const data = getValues();

                files.forEach((file, index) => {
                    file.contractName = data[`name-${index}`];
                    file.contractStatus = data[`status-${index}`].value;
                    file.contractType = data[`type-${index}`].value;
                });

                onComplete({ files }, reference);
            }
            setNextStep(true);
        } else {
            setError(true);
        }
    };

    const handleBackBtn = () => {
        if (nextStep) {
            setNextStep(false);
        } else {
            handleBack();
            setFiles([]);
        }
    };
    return (
        <StepContainer
            className="sidebar-new-contract-height"
            title={t('contracts.new.source_file_multiple')}
            form={
                nextStep ? (
                    <UploadForm
                        files={files}
                        register={register}
                        control={control}
                        errors={errors}
                        handleNext={handleNext}
                        handleRefChange={handleRefChange}
                        reference={reference}
                        showReference={showReference}
                    />
                ) : (
                    <UploadDocumentZone error={error} setError={setError} setFiles={setFiles} files={files} />
                )
            }
            footer={
                <>
                    <BackBtn onClick={handleBackBtn} label={t('form.exit')} />
                    <NextBtn onClick={nextStep ? handleSubmit(handleNext) : handleNext} label={t('contracts.listing.page_next')} />
                </>
            }
        />
    );
};

export default MultipleStep;
